<script setup>

    import { ChatBubbleOvalLeftEllipsisIcon, PhoneArrowUpRightIcon } from '@heroicons/vue/24/outline'
    import { IconDocumentSave20Regular, IconPersonSearch16Filled } from '@iconify-prerendered/vue-fluent'
    import { computed, ref, watch, nextTick } from 'vue'
    import LatteCaregiverBasicInfo from '@/Components/Caregiver/LatteCaregiverBasicInfo.vue'
    import LatteSearchScheduleMatching from '@/Components/Caregiver/LatteSearchScheduleMatching.vue'
    import CaregiverSearchResultCriteriasAndNotes from '@/Components/Caregiver/CaregiverSearchResultCriteriasAndNotes.vue'
    import CaregiverSearchResultSchedule from '@/Components/Caregiver/CaregiverSearchResultSchedule.vue'
    import { ArrowTopRightOnSquareIcon } from '@heroicons/vue/24/outline'
    import { useSmsStore } from '@/Stores'
    import { ExclamationTriangleIcon, ArrowLongRightIcon } from '@heroicons/vue/16/solid'
    import { handleBlur } from '@/Helpers'
    import { criteriaMatchesCaregiver, getMatchingCaregiverCriteria } from '@/Utils/criteriaMatching'

    const smsStore = useSmsStore()
    const { addToSms } = smsStore

    const caregiverCoffeeBaseUrl = import.meta.env.VITE_COFFEE_BASEURL + '/user/recruiter-candidates/personalinfo/id/'

    const props = defineProps({
        caregivers: {
            type: Array,
            required: true,
        },
        selectedCaregiverStore: {
            type: Object,
            required: false,
            default: null,
        },
        currentSearchState: {
            type: Object,
            required: false,
            default: null,
        },
        limitOffset: {
            type: Object,
            required: true,
        },
        searchFilterChanged: {
            type: Boolean,
            required: true,
        },
        isSearching: {
            type: Boolean,
            required: true,
        },
        isRequestPending: {
            type: Boolean,
            required: true,
        },
        noMoreResult: {
            type: Boolean,
            required: false,
            default: false,
        },
        isLoadingClient: {
            type: Boolean,
            required: false,
            default: false,
        },
        totalCount: {
            type: Number,
            required: false,
            default: 0,
        },
    })

    const tableColumns = [
        {
            label: 'Caregiver',
            align: 'left',
            width: 'w-[40%]',
        },
        {
            label: 'Schedule',
            align: 'left',
            width: 'w-[30%]',
        },
        {
            label: 'Location',
            align: 'center',
            width: 'w-[15%]',
        },
        {
            label: '',
            align: 'left',
            width: 'w-[15%]',
        },
    ]

    const emit = defineEmits(['selectCaregiver', 'openQuickSearch', 'relaxMustHaves', 'increaseTravelTime']);

    const travelTimeSteps = ['20', '40', '60', '120']

    const canIncreaseTravelTime = computed(() => {
        const current = props.currentSearchState?.max_travel_time
        return current === '20' || current === '40' || current === '60'
    })

    const nextTravelTimeLabel = computed(() => {
        const current = props.currentSearchState?.max_travel_time
        const currentIndex = travelTimeSteps.indexOf(current)
        if (currentIndex === -1 || currentIndex >= travelTimeSteps.length - 1) return null
        return travelTimeSteps[currentIndex + 1] + ' min'
    })

    const hasMustHaveCriteria = computed(() => {
        return Array.isArray(props.currentSearchState?.must_have) && props.currentSearchState.must_have.length > 0
    })

    const searchDays = computed(() => {
        if (props.currentSearchState === null) {
            return [];
        }

        if (props.currentSearchState.living_preference === '') {
            return [];
        }
        if (!props.currentSearchState.availability || props.currentSearchState.availability.length === 0) {
            return [];
        }
        return props.currentSearchState.availability.map(slot => slot.day)
    })

    const firstLoad = computed(() => {
        return props.currentSearchState === null;
    })
    const showResultsTable = computed(() => {
        if (firstLoad.value) {
            return false;
        }

        if (props.isRequestPending) {
            if (props.limitOffset.offset === 0 && props.searchFilterChanged) {
                return false;
            }
        }

        return true;
    })

    // Phone actions related logics
    const caregiverCellPhoneDict = computed(() => {
        const dict = {}
        props.caregivers.forEach(caregiver => {
            const cellPhone = caregiver.phone_numbers.find(p => p.type === 'cell')
            if (cellPhone) {
                dict[caregiver.id] = cellPhone.number
            }
        })
        return dict;
    })

    const caregiverPrimaryPhoneDict = computed(() => {
        const dict = {}
        props.caregivers.forEach(caregiver => {
            const byType = {}
            caregiver.phone_numbers.forEach(p => { byType[p.type] = p })
            const phone = byType.cell || byType.home || byType.work || null
            if (phone) {
                dict[caregiver.id] = phone
            }
        })
        return dict
    })

    // Expanded caregiver details logic
    const activeTab = ref('caregiver')
    const selectedCaregiver = computed(() => {
        return props.selectedCaregiverStore ? props.selectedCaregiverStore.caregiver : {};
    })

    const toggleExpansion = async (caregiverId, tab = 'caregiver') => {
        if (selectedCaregiver.value.id === caregiverId && activeTab.value === tab) {
            activeTab.value = 'caregiver'
            emit('selectCaregiver', null)
        } else {
            activeTab.value = tab
            emit('selectCaregiver', caregiverId)

            await nextTick()

            const element = document.getElementById(`caregiver-row-${caregiverId}`)
            if (element) {
                const rect = element.getBoundingClientRect()
                const verticalOffset = 60
                if (rect.top < verticalOffset || rect.bottom > window.innerHeight) {
                    const offsetPosition = rect.top + window.pageYOffset - verticalOffset
                    window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth'
                    })
                }
            }
        }
    }

    // matching criteria data
    const matchingCriterias = computed(() => {
        const wowFactors = Array.isArray(props.currentSearchState?.wow) ? props.currentSearchState.wow : [];
        const mustHaveFactors = Array.isArray(props.currentSearchState?.must_have) ? props.currentSearchState.must_have : [];
        const result = {};
        props.caregivers.forEach(caregiver => {
            const caregiverCriteria = Array.isArray(caregiver.matching_criterias) ? caregiver.matching_criterias : [];
            result[caregiver.id] = {
                wow: wowFactors
                    .map(wow => getMatchingCaregiverCriteria(wow, caregiver, caregiverCriteria))
                    .filter(Boolean),
                mustHave: mustHaveFactors
                    .map(mh => getMatchingCaregiverCriteria(mh, caregiver, caregiverCriteria))
                    .filter(Boolean),
            };
        });
        return result;
    })

    const missingCriterias = computed(() => {
        const result = {};
        props.caregivers.forEach(caregiver => {
            const caregiverCriteria = Array.isArray(caregiver.matching_criterias) ? caregiver.matching_criterias : [];
            result[caregiver.id] = Array.isArray(props.currentSearchState?.wow)
                ? props.currentSearchState.wow.filter(wow => !criteriaMatchesCaregiver(wow, caregiver, caregiverCriteria))
                : [];
        });
        return result;
    })

    function getGoogleMapsUrl(caregiver) {
        const caregiverAddressParts = [
            caregiver.address.address,
            caregiver.address.city,
            caregiver.address.state,
            caregiver.address.zip,
        ].filter(Boolean)

        const destination = props.currentSearchState?.destination
        const clientAddressParts = destination ? [
            destination.address,
            destination.city,
            destination.state,
            destination.zip,
        ].filter(Boolean) : []

        if (caregiverAddressParts.length > 0 && clientAddressParts.length > 0) {
            const origin = caregiverAddressParts.join(', ')
            const dest = clientAddressParts.join(', ')
            return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(dest)}`
        }

        const query = caregiverAddressParts.join(', ')
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
    }

    watch(() => props.isRequestPending, (searching) => {
        if (searching && props.limitOffset.offset === 0) {
            emit('selectCaregiver', null)
            activeTab.value = 'caregiver'
        }
    }, { immediate: true })

</script>

<template>
    <div
        v-if="(firstLoad && !isSearching) || props.isLoadingClient"
        class="flex flex-col items-center justify-center min-h-[calc(100vh-theme(spacing.16))] gap-6 text-center"
    >
        <template v-if="props.isLoadingClient">
            <div class="h-10 w-10 border-2 border-t-transparent border-lifeworx-blue-600 rounded-full animate-spin"></div>
            <div class="font-semibold text-lifeworx-blue-600 text-lg">
                Loading client...
            </div>
        </template>
        <template v-else>
            <div class="font-bold text-lifeworx-blue-800 text-xl">
                Start Your Search
            </div>
        <button
            class="flex flex-col items-center justify-center gap-4 px-16 py-12 rounded-xl border-2 border-dashed border-lifeworx-blue-600 bg-lifeworx-blue-600/10 hover:bg-lifeworx-blue-600/20 cursor-pointer transition-colors"
            @click="emit('openQuickSearch')"
        >

            <IconPersonSearch16Filled class="h-12 w-12 text-lifeworx-blue-600" />
            <span class="text-base font-semibold text-lifeworx-blue-600">
                Choose a Lead / Prospect / Client
            </span>
        </button>
        <div class="text-stone-500 font-semibold">
            or begin selecting criteria
        </div>
        </template>
    </div>
    <div
        v-if="props.limitOffset.offset == 0 && isRequestPending"
        class="flex flex-col items-center justify-center min-h-[calc(100vh-theme(spacing.32))] gap-2 text-lg text-lifeworx-blue-600 font-semibold"
    >
        <div class="h-10 w-10 border-2 border-t-transparent border-t-lifeworx-blue-600 rounded-full animate-spin"></div>
        <div class="flex items-center">
            Searching...
        </div>
    </div>
    <div
        v-if="showResultsTable && caregivers.length === 0 && !isRequestPending"
        class="flex flex-col items-center justify-center min-h-[calc(100vh-theme(spacing.32))] gap-8 text-lg"
    >
        <div class="text-center text-stone-500 font-semibold ">
            No caregivers found matching your search parameters.<br/>
            Try adjusting your criteria.
        </div>
        <div v-if="hasMustHaveCriteria || canIncreaseTravelTime"
            class="flex flex-col items-center gap-1"
        >
            <span class="text-base font-bold text-lifeworx-blue-600">SUGGESTIONS:</span>
            <button
                v-if="hasMustHaveCriteria"
                class="mt-4 px-6 py-3 rounded-lg border-2 border-lifeworx-orange-600 text-lifeworx-orange-600 bg-lifeworx-orange-600/5 hover:text-lifeworx-purple-600 font-bold text-sm hover:border-lifeworx-purple-600 hover:bg-lifeworx-purple-600/20 transition-colors"
                @click="emit('relaxMustHaves')"
            >
                Relax Must-Haves <ArrowLongRightIcon class="-mt-0.5 inline-flex h-5 w-5" aria-hidden="true" /> Wow-Factors
            </button>
            <button
                v-if="canIncreaseTravelTime"
                class="mt-4 px-6 py-3 rounded-lg border-2 border-lifeworx-blue-600 text-lifeworx-blue-600 bg-lifeworx-blue-600/5 hover:text-lifeworx-blue-600 font-bold text-sm hover:border-lifeworx-blue-600 hover:bg-lifeworx-blue-600/20 transition-colors"
                @click="emit('increaseTravelTime')"
            >
                Increase Max Travel Time to {{ nextTravelTimeLabel }}
            </button>
        </div>

    </div>
    <div
        v-if="showResultsTable && caregivers.length > 0 && totalCount > 0"
        class="flex justify-between items-center mt-4 mb-2 px-4 text-sm text-stone-600"
    >
        <div>
            Showing {{ caregivers.length }} of {{ totalCount }} caregivers
        </div>
    </div>
    <table
        v-if="showResultsTable && caregivers.length > 0"
        class="table-fixed w-full text-left relative top-10"
    >
        <thead class="sticky top-16 bg-gray-100 border-b z-module-container shadow">
            <tr>
                <th
                    v-for="column in tableColumns"
                    :class="[
                        'text-sm uppercase text-stone-500 font-semibold py-3 px-4',
                        column.width,
                        {
                            'text-center': column.align === 'center',
                        }
                    ]"
                >
                    {{ column.label }}
                </th>
            </tr>
        </thead>
        <tbody class="relative top-6">
            <template
                v-for="(caregiver, index) in props.caregivers"
                :key="caregiver.id"
            >
                <!-- Regular row -->
                <tr
                    :id="`caregiver-row-${caregiver.id}`"
                    class="transition-colors scroll-mt-4"
                    :class="[
                        { 'bg-lifeworx-blue-100/10': selectedCaregiver.id === caregiver.id },
                        { 'bg-stone-100': (selectedCaregiver.id !== caregiver.id) && (index % 2 === 1) },
                        { 'hover:bg-[#dceef395]': selectedCaregiver.id !== caregiver.id }
                    ]"
                >
                    <td
                        class="px-4 hover:bg-[#dceef3] cursor-pointer"
                        :class="{
                            'bg-[#C3E2EA]': selectedCaregiver.id === caregiver.id && activeTab === 'caregiver'
                        }"
                    >
                        <div
                            class="relative cursor-pointer"
                            @click="toggleExpansion(caregiver.id, 'caregiver')"
                        >
                            <div class="relative">
                                <LatteCaregiverBasicInfo
                                    :caregiver="caregiver"
                                    :badge="{ value: caregiver.wow_match_count }"
                                    :job="caregiver.client_job"
                                    :ringColor="caregiver.client_job?.latest_rating?.status"
                                />
                            </div>
                        </div>
                    </td>
                    <td
                        class="px-4 text-center hover:bg-[#dceef3] cursor-pointer"
                        :class="{
                            'bg-[#C3E2EA]': selectedCaregiver.id === caregiver.id && activeTab === 'schedule'
                        }"
                    >
                        <div
                            class="relative cursor-pointer h-full max-w-80"
                            @click="toggleExpansion(caregiver.id, 'schedule')"
                        >
                            <div class="relative p-2">
                                <LatteSearchScheduleMatching
                                    :care_schedule_search="caregiver.care_schedule_search"
                                    :search_enable_days="searchDays"
                                />
                                <span
                                    v-if="props.currentSearchState?.alternate_weekends_only && caregiver.availability_pref_alternate_weekends_only"
                                    class="text-sm font-semibold"
                                >
                                    Alt. Weekends Only
                                </span>
                            </div>
                        </div>
                    </td>
                    <td class="px-4 text-center hover:bg-[#dceef3]">
                        <a
                            :href="getGoogleMapsUrl(caregiver)"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="relative cursor-pointer h-full block"
                        >
                            <div>
                                <div class="relative p-2">
                                    <span class="whitespace-nowrap font-semibold text-xs text-lifeworx-blue-800">
                                        {{ caregiver.address.city }}{{ (caregiver.address.city && caregiver.address.state) ? ', ' : '' }}{{ caregiver.address.state }}
                                    </span>
                                    <br />
                                    <template v-if="caregiver.travel_time">
                                        <VTooltip
                                            v-if="caregiver.availability_pref_max_travel_time && caregiver.travel_time > caregiver.availability_pref_max_travel_time"
                                            @apply-hide="handleBlur"
                                        >
                                            <span class="text-sm text-lifeworx-orange-600 font-semibold">
                                                {{ Math.round(caregiver.travel_time) }} min
                                                <ExclamationTriangleIcon class="inline ml-1 h-5 w-5 text-lifeworx-orange-600" />
                                            </span>
                                            <template #popper>
                                                <span class="text-sm">
                                                    Travel time exceeds preferred max of {{ caregiver.availability_pref_max_travel_time }} min
                                                </span>
                                            </template>
                                        </VTooltip>
                                        <span v-else class="text-sm text-stone-500">
                                            {{ Math.round(caregiver.travel_time) }} min
                                        </span>
                                    </template>
                                </div>
                            </div>
                        </a>
                    </td>
                    <td class="py-3 px-4">
                        <div class="flex gap-2">
                            <a
                                :href="caregiverCoffeeBaseUrl + caregiver.coffee_job_profile_id"
                                target="_blank"
                            >
                                <ArrowTopRightOnSquareIcon
                                    class="h-6 w-6 text-lifeworx-blue-600 hover:text-lifeworx-blue-500"
                                    aria-hidden="true"
                                />
                            </a>
                            <template v-if="caregiverPrimaryPhoneDict[caregiver.id]">
                                <a
                                    class="text-lifeworx-blue-800 hover:text-lifeworx-blue-500"
                                    :href="'tel:' + caregiverPrimaryPhoneDict[caregiver.id].country_code + caregiverPrimaryPhoneDict[caregiver.id].number"
                                >
                                    <PhoneArrowUpRightIcon
                                        class="h-6 w-6 shrink-0"
                                        aria-hidden="true"
                                    />
                                </a>
                            </template>
                            <ChatBubbleOvalLeftEllipsisIcon
                                v-if="caregiverCellPhoneDict[caregiver.id] != null"
                                class="text-lifeworx-blue-800 h-6 w-6 shrink-0 hover:text-lifeworx-blue-500 cursor-pointer"
                                aria-hidden="true"
                                @click="() => addToSms(caregiverCellPhoneDict[caregiver.id])"
                            />
                            <a
                                class="text-lifeworx-blue-800 hover:text-lifeworx-blue-500"
                                :href="`/caregivers/${caregiver.id}/bio-pdf`"
                                target="_blank"
                            >
                                <IconDocumentSave20Regular
                                    class="h-6 w-6 shrink-0"
                                    aria-hidden="true"
                                />
                            </a>
                        </div>
                    </td>
                </tr>

                <!-- Expanded details row -->
                <tr v-if="selectedCaregiver.id === caregiver.id">
                    <td
                        colspan="4"
                        class="p-0"
                    >
                        <div class="bg-transparent">
                            <div class="min-h-[300px] bg-[#C3E2EA] p-4">
                                <div class="space-y-4">
                                    <CaregiverSearchResultSchedule
                                        v-if="activeTab === 'schedule'"
                                        :caregiver="selectedCaregiver"
                                        :searchLivingPreference="props.currentSearchState.living_preference"
                                        :searchSchedule="props.currentSearchState.availability"
                                    />
                                    <CaregiverSearchResultCriteriasAndNotes
                                        v-if="activeTab === 'caregiver'"
                                        :caregiverStore="props.selectedCaregiverStore"
                                        :matchingWowCriteria="matchingCriterias[caregiver.id]?.wow || []"
                                        :matchingMustHaveCriteria="matchingCriterias[caregiver.id]?.mustHave || []"
                                        :missingCriteria="missingCriterias[caregiver.id] || []"
                                    />
                                </div>
                            </div>
                        </div>
                    </td>
                </tr>
            </template>
        </tbody>
    </table>
    <div
        v-if="props.limitOffset.offset > 0 && isRequestPending"
        class="flex flex-col items-center justify-center py-32 gap-2 text-lg text-lifeworx-blue-600 font-semibold"
    >
        <div class="h-10 w-10 border-2 border-t-transparent border-t-lifeworx-blue-600 rounded-full animate-spin"></div>
        <div class="flex items-center">
            Loading more...
        </div>
    </div>
    <div
        v-if="showResultsTable && caregivers.length > 0 && noMoreResult && !isRequestPending"
        class="flex items-center justify-center py-32 text-sm text-center text-stone-500"
    >
        Found {{ caregivers.length }} Matching Caregivers<br/><br/>End of Results
    </div>
    <div class="h-24" />

</template>
